import React, { useState, useEffect, useCallback } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import { useNavigate, useParams } from "react-router-dom";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { GoArrowLeft } from "react-icons/go";
import { HiOutlinePlusSm } from "react-icons/hi";

export const AdminAthleteEdit = () => {
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();
  const navigate = useNavigate();

  const [photoPreview, setPhotoPreview] = useState(null);
  const [photo, setPhoto] = useState(null);

  const [activeTab, setActiveTab] = useState("personal");

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    phonenumber: "",
    email: "",
    password: "",
    training_preference: "",
    sport: "",
    sport_other: "",
    city: "",
    state: "",
    dob: "",
    gender: "",
    height_feet: "",
    height_inches: "",
    weight: "",
    pr_100m: "",
    pr_200m: "",
    pr_400m: "",
    intensity: "",
    account_type: "FREE PLAN",
    status: "Pending",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Format date properly
  const formatDateForStorage = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  // Fetch athlete data
  const fetchAthleteData = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL.GET_ATHLETE_BY_ID}${athlete_id}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );
      const data = await response.json();

      if (response.ok) {
        const formattedDOB = formatDateForStorage(data.dob);
        setFormData((prev) => ({
          ...prev,
          ...data,
          password: "", // ← always reset password
          dob: formattedDOB,
          intensity: data.intensity || "",
          account_type: data.account_type || "FREE PLAN",
          status: data.status || "Pending",
          sport_other: data.sport_other || "",
          height_feet: data.height_feet || "",
          height_inches: data.height_inches || "",
        }));
        if (data.photo)
          setPhotoPreview(API_URL.PROFILE_PICTURE_URL + data.photo);

        setNotes(data.notes || "");
      } else {
        throw new Error(data.error || "Failed to fetch athlete data");
      }
    } catch (err) {
      setError(err.message);
    }
  }, [athlete_id, staff.token]);

  useEffect(() => {
    fetchAthleteData();
  }, [fetchAthleteData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      dob: date ? date.toISOString() : null,
    }));
  };

  const handlePhotoChange = (e) => {
    if (e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setPhoto(selectedFile);
      setPhotoPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const formPayload = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "dob") {
          formPayload.append("dob", formatDateForStorage(formData.dob));
        } else if (key === "password") {
          if (formData.password.trim() !== "") {
            formPayload.append("password", formData.password);
          }
        } else {
          formPayload.append(key, formData[key]);
        }
      });

      if (photo) formPayload.append("photo", photo);

      const response = await fetch(
        `${API_URL.UPDATE_ATHLETE_DETAILS_BY_ID}${athlete_id}`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${staff.token}` },
          body: formPayload,
        },
      );

      const data = await response.json();
      setLoading(false);

      if (!response.ok) {
        setError(data.error || "Failed to update athlete profile");
      } else {
        setSuccess("Athlete profile updated successfully!");
        fetchAthleteData();
      }
    } catch (err) {
      setLoading(false);
      setError(err.message);
    }
  };

  const ACCOUNT_TABS = [
    { label: "Access", value: "FREE PLAN" },
    { label: "Programs", value: "STATIC PLAN" },
    { label: "Coached", value: "AMAX COACHED" },
  ];

  /* payment transactions logic */
  const [transactions, setTransactions] = useState({
    speed: [],
    strength: [],
    tools: [],
  });
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState("");

  const fetchPaymentTransactions = useCallback(async () => {
    if (!staff || !athlete_id) return;

    setTransactionsLoading(true);
    try {
      const res = await fetch(
        `${API_URL.GET_ATHLETE_PURCHASE_DATA_ADMIN}${athlete_id}`,
        {
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );

      const data = await res.json();

      if (res.ok) {
        setTransactions({
          speed: data.data?.speed || [],
          strength: data.data?.strength || [],
          tools: data.data?.tools || [],
        });
        setTransactionsError("");
      } else {
        throw new Error(data.error || "Failed to fetch transactions");
      }
    } catch (err) {
      setTransactionsError("Failed to load transactions");
    } finally {
      setTransactionsLoading(false);
    }
  }, [athlete_id, staff]);

  useEffect(() => {
    if (activeTab === "purchases") {
      fetchPaymentTransactions();
    }
  }, [activeTab, fetchPaymentTransactions]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString() + " " + d.toLocaleTimeString();
  };

  const renderTransactionList = (list, type) => {
    if (!list || list.length === 0) return null;

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">{type}</h3>
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden bg-white">
          {list.map((tx) => (
            <li
              key={tx._id}
              className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center"
            >
              <div className="flex flex-col sm:flex-row sm:gap-4">
                <p className="text-sm font-semibold">
                  {type === "Tools"
                    ? `Tool: ${tx.title}`
                    : `Template: ${tx.title || tx.template_id}`}
                </p>
                <p className="text-gray-700">Price: ${tx.price}</p>
                <p className="text-gray-700">
                  Transaction ID: {tx.payment_transaction_id}
                </p>
                <p className="text-gray-700">Status: {tx.payment_status}</p>
              </div>
              <div className="mt-2 sm:mt-0 text-gray-500 text-sm">
                Created: {formatDate(tx.createdAt)}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [savedNotes, setSavedNotes] = useState(false);

  const handleSaveNotes = async () => {
    if (!athlete_id) return;

    setSavingNotes(true);

    try {
      const response = await fetch(API_URL.SAVE_ATHLETE_NOTES, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({
          athlete_id,
          notes, // ✅ ONLY notes
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSavedNotes(true);
        setTimeout(() => setSavedNotes(false), 2000);
      } else {
        console.error(data.error || "Failed to save notes");
      }
    } catch (err) {
      console.error("Error saving notes:", err);
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div className="flex md:h-screen">
      <StaffSideMenu />

      <div className="max-md:w-full md:flex-1 bg-bgBase overflow-auto p-5 md:p-10">
        <div className="w-full max-w-[1136px] mx-auto">
          <div className="space-y-6 mb-7">
            <button
              onClick={() => navigate(-1)}
              className="text-slate100 font-inter font-normal text-sm leading-tight flex items-center gap-2 hover:underline max-md:ml-12"
            >
              <GoArrowLeft color="#666666" /> Back to Athletes
            </button>
            <div className="flex max-lg:flex-col justify-between lg:items-center gap-5 mb-6">
              <h1 className="font-inter font-medium text-dark text-3xl !leading-none">
                Edit Profile Details
              </h1>

              <div className="flex max-sm:flex-col items-center gap-2">
                <select
                  name="account_type"
                  value={formData.account_type}
                  onChange={handleChange}
                  className="w-full min-w-52 outline-none bg-white border border-tblBorder p-3 rounded-10 text-sm font-inter font-normal leading-tight"
                >
                  {ACCOUNT_TABS.map((tab) => (
                    <option key={tab.value} value={tab.value}>
                      {tab.label}
                    </option>
                  ))}
                </select>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full min-w-52 outline-none bg-white border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight capitalize"
                >
                  <option value="Lead">Lead</option>
                  <option value="Follow Up">Follow Up</option>
                  <option value="Pending">Pending</option>
                  <option value="Member">Member</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          {/* error message */}
          {error && (
            <div className="bg-red-500/10 p-3 rounded-10 my-3">
              {" "}
              <p className="text-red-500 mb-4 font-inter font-medium text-sm">
                {error}
              </p>
            </div>
          )}
          {success && (
            <div className="bg-green-600/10 p-3 rounded-10 my-3">
              <p className="text-green-600 font-inter font-medium text-sm">
                {success}
              </p>
            </div>
          )}
          {loading && (
            <div className="bg-yellow-600/10 p-3 rounded-10 my-3">
              <p className="text-yellow-500 mb-4 font-inter font-medium text-sm">
                Processing, please wait...
              </p>
            </div>
          )}
          {/* tabbing */}
          <div className="flex gap-8 border-b border-tblBorder mb-6">
            <button
              type="button"
              onClick={() => setActiveTab("personal")}
              className={`pb-3 text-sm font-inter font-medium leading-none border-b-2 border-transparent  hover:text-dark hover:border-dark transition ${
                activeTab === "personal"
                  ? "!border-darkYellow text-dark"
                  : "text-slate100"
              }`}
            >
              Personal
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("training")}
              className={`pb-3 text-sm font-inter font-medium leading-none border-b-2 border-transparent  hover:text-dark hover:border-dark transition ${
                activeTab === "training"
                  ? "!border-darkYellow text-dark"
                  : "text-slate100"
              }`}
            >
              Training
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("purchases")}
              className={`pb-3 text-sm font-inter font-medium leading-none border-b-2 border-transparent  hover:text-dark hover:border-dark transition ${
                activeTab === "purchases"
                  ? "!border-darkYellow text-dark"
                  : "text-slate100"
              }`}
            >
              Purchases
            </button>
          </div>
          {/* table */}
          <form onSubmit={handleSubmit} className="space-y-3 md:space-y-6">
            {/* personal info */}
            {activeTab === "personal" && (
              <>
                <div className="bg-white border border-tblBorder p-5 md:p-8 rounded-14">
                  {/* Personal Information content stays EXACT same */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                    <div className="block">
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div className="block">
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div className="block">
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        name="phonenumber"
                        value={formData.phonenumber}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div className="block">
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        readOnly
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div className="block">
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Password
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div className="block md:col-span-2">
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Profile Photo
                      </label>
                      <div className="flex items-center gap-2 mt-2">
                        {photoPreview && (
                          <img
                            src={photoPreview}
                            alt="Profile"
                            className="rounded-full shadow-md border border-tblBorder w-20 h-20"
                          />
                        )}
                        <div>
                          <input
                            type="file"
                            onChange={handlePhotoChange}
                            placeholder="Upload Photo"
                            className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                            accept="image/*"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-tblBorder p-5 md:p-8 rounded-14 mt-6">
                  <h3 className="text-dark font-inter font-medium text-lg mb-4">
                    General Notes
                  </h3>

                  <div className="mb-6">
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      className="w-full border border-tblBorder py-3 px-4 rounded-10 font-inter font-normal text-base outline-none"
                      placeholder="Enter general notes..."
                    />

                    <div className="flex justify-start mt-4 items-center gap-3">
                      <button
                        type="button"
                        onClick={handleSaveNotes}
                        disabled={savingNotes}
                        className="px-6 py-2.5 flex items-center gap-2 bg-dark text-white font-inter font-normal text-sm leading-tight capitalize rounded-lg hover:bg-black"
                      >
                        <HiOutlinePlusSm color="#fff" size={16} />{" "}
                        {savingNotes ? "Saving..." : "Save Notes"}
                      </button>

                      {savedNotes && (
                        <span className="text-green-600 text-sm">Saved ✓</span>
                      )}
                    </div>
                  </div>

                 {/* <div className="bg-bgBase rounded-10 p-6 space-y-3">
                    <p class="text-dark font-inter font-normal text-base">
                      Athlete showed great improvement in sprint times.
                      Recommend focusing on strength training.
                    </p>
                    <p class="text-slate100 font-inter font-medium text-sm">
                      Coach Mike <span class="mx-2">•</span>{" "}
                      <span class="font-inter font-normal">
                        March 30, 2026 at 2:45 PM
                      </span>
                    </p>
                  </div>
                  */}
                </div>
              </>
            )}

            {activeTab === "training" && (
              <>
                {/* Training Preferences */}
                <div className="space-y-3 md:space-y-6 bg-white border border-tblBorder p-5 md:p-8 rounded-14">
                  <h3 className="col-span-2 text-dark font-inter font-medium text-lg capitalize">
                    Training Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Training Preference
                      </label>
                      <select
                        name="training_preference"
                        value={formData.training_preference}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      >
                        <option value="">Select</option>
                        <option value="on-site">ON-SITE TRAINING</option>
                        <option value="online">REMOTE TRAINING</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Sport
                      </label>
                      <select
                        name="sport"
                        value={formData.sport}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      >
                        <option value="">Select</option>
                        <option value="Track and Field">Track and Field</option>
                        <option value="Football">Football</option>
                        <option value="Baseball">Baseball</option>
                        <option value="Softball">Softball</option>
                        <option value="Soccer">Soccer</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {formData.sport === "Other" && (
                      <div>
                        <label className="block font-inter text-slate100 text-sm font-medium">
                          Other Sport
                        </label>
                        <input
                          type="text"
                          name="sport_other"
                          value={formData.sport_other}
                          onChange={handleChange}
                          placeholder="Specify other sport"
                          className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        City
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        State
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Date of Birth
                      </label>
                      <DatePicker
                        selected={formData.dob ? new Date(formData.dob) : null}
                        onChange={handleDateChange}
                        dateFormat="MM-dd-yyyy"
                        showYearDropdown
                        dropdownMode="select"
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Gender
                      </label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Physical Stats */}
                <div className="space-y-3 md:space-y-6 bg-white border border-tblBorder p-5 md:p-8 rounded-14">
                  <h3 className="col-span-2 text-dark font-inter font-medium text-lg capitalize">
                    Physical Stats
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                    <div className="flex max-lg:flex-col gap-3">
                      <div className="flex-1">
                        <label className="block font-inter text-slate100 text-sm font-medium">
                          Height (Feet)
                        </label>
                        <input
                          type="number"
                          name="height_feet"
                          value={formData.height_feet}
                          onChange={handleChange}
                          className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                        />
                      </div>

                      <div className="flex-1">
                        <label className="block font-inter text-slate100 text-sm font-medium">
                          Height (Inches)
                        </label>
                        <input
                          type="number"
                          name="height_inches"
                          value={formData.height_inches}
                          onChange={handleChange}
                          className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Weight (lbs)
                      </label>
                      <input
                        type="number"
                        name="weight"
                        value={formData.weight}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Intensity
                      </label>
                      <input
                        type="text"
                        name="intensity"
                        value={formData.intensity}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    {/* <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Account Type
                      </label>
                      <select
                        name="account_type"
                        value={formData.account_type}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      >
                        <option value="FREE PLAN">FREE PLAN</option>
                        <option value="STATIC PLAN">STATIC PLAN</option>
                        <option value="AMAX COACHED">AMAX COACHED</option>
                      </select>
                    </div>
                    */}
                  </div>
                </div>

                {/* Performance Records */}
                <div className="space-y-3 md:space-y-6 bg-white border border-tblBorder p-5 md:p-8 rounded-14">
                  <h3 className="col-span-2 text-dark font-inter font-medium text-lg capitalize">
                    Performance Records
                  </h3>
                  <div className="md:grid md:grid-cols-3 max-md:space-y-6 gap-3 md:gap-6">
                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Goal 100m
                      </label>
                      <input
                        type="text"
                        name="pr_100m"
                        value={formData.pr_100m}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Goal 200m
                      </label>
                      <input
                        type="text"
                        name="pr_200m"
                        value={formData.pr_200m}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>

                    <div>
                      <label className="block font-inter text-slate100 text-sm font-medium">
                        Goal 400m
                      </label>
                      <input
                        type="text"
                        name="pr_400m"
                        value={formData.pr_400m}
                        onChange={handleChange}
                        className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>
                  </div>
                </div>

                {/* notes */}
                <div className="bg-white border border-tblBorder p-5 md:p-8 rounded-14 mt-6">
                  <h3 className="text-dark font-inter font-medium text-lg mb-4">
                    General Notes
                  </h3>

                  <div className="mb-6">
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      className="w-full border border-tblBorder py-3 px-4 rounded-10 font-inter font-normal text-base outline-none"
                      placeholder="Enter general notes..."
                    />

                    <div className="flex justify-start mt-4 items-center gap-3">
                      <button
                        type="button"
                        onClick={handleSaveNotes}
                        disabled={savingNotes}
                        className="px-6 py-2.5 flex items-center gap-2 bg-dark text-white font-inter font-normal text-sm leading-tight capitalize rounded-lg hover:bg-black"
                      >
                        <HiOutlinePlusSm color="#fff" size={16} />{" "}
                        {savingNotes ? "Saving..." : "Save Notes"}
                      </button>

                      {savedNotes && (
                        <span className="text-green-600 text-sm">Saved ✓</span>
                      )}
                    </div>
                  </div>

                {/*  <div className="bg-bgBase rounded-10 p-6 space-y-3">
                    <p class="text-dark font-inter font-normal text-base">
                      Athlete showed great improvement in sprint times.
                      Recommend focusing on strength training.
                    </p>
                    <p class="text-slate100 font-inter font-medium text-sm">
                      Coach Mike <span class="mx-2">•</span>{" "}
                      <span class="font-inter font-normal">
                        March 30, 2026 at 2:45 PM
                      </span>
                    </p>
                  </div>
           */ }
                </div>
              </>
            )}

            {activeTab === "purchases" && (
              <div className="bg-white border border-tblBorder p-5 md:p-8 rounded-14 font-inter font-normal">
                <h2 className="font-inter font-medium text-dark text-xl mb-4">
                  Purchase Transactions
                </h2>

                {transactionsLoading ? (
                  <div className="bg-bgBase p-4 rounded-10">
                    <p>Loading transactions...</p>
                  </div>
                ) : transactionsError ? (
                  <div className="bg-red-100 p-4 rounded-10">
                    <p className="text-red-500">{transactionsError}</p>
                  </div>
                ) : !transactions.speed.length &&
                  !transactions.strength.length &&
                  !transactions.tools.length ? (
                  <div className="bg-bgBase p-4 rounded-10">
                    <p>No transactions available.</p>
                  </div>
                ) : (
                  <div className="space-y-6 max-h-[500px] overflow-y-auto">
                    {renderTransactionList(
                      transactions.speed,
                      "Speed Templates",
                    )}
                    {renderTransactionList(
                      transactions.strength,
                      "Strength Templates",
                    )}
                    {renderTransactionList(transactions.tools, "Tools")}
                  </div>
                )}

                {/* notes */}
                <div className="mt-6">
                  <h3 className="text-dark font-inter font-medium text-lg mb-4">
                    General Notes
                  </h3>

                  <div className="mb-6">
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      className="w-full border border-tblBorder py-3 px-4 rounded-10 font-inter font-normal text-base outline-none"
                      placeholder="Enter general notes..."
                    />

                    <div className="flex justify-start mt-4 items-center gap-3">
                      <button
                        type="button"
                        onClick={handleSaveNotes}
                        disabled={savingNotes}
                        className="px-6 py-2.5 flex items-center gap-2 bg-dark text-white font-inter font-normal text-sm leading-tight capitalize rounded-lg hover:bg-black"
                      >
                        <HiOutlinePlusSm color="#fff" size={16} />{" "}
                        {savingNotes ? "Saving..." : "Save Notes"}
                      </button>

                      {savedNotes && (
                        <span className="text-green-600 text-sm">Saved ✓</span>
                      )}
                    </div>
                  </div>

                  {/*<div className="bg-bgBase rounded-10 p-6 space-y-3">
                    <p class="text-dark font-inter font-normal text-base">
                      Athlete showed great improvement in sprint times.
                      Recommend focusing on strength training.
                    </p>
                    <p class="text-slate100 font-inter font-medium text-sm">
                      Coach Mike <span class="mx-2">•</span>{" "}
                      <span class="font-inter font-normal">
                        March 30, 2026 at 2:45 PM
                      </span>
                    </p>
                  </div>*/}
                </div>
              </div>
            )}

            {activeTab !== "purchases" && (
              <div className="text-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black"
                >
                  {loading ? "Updating..." : "Save Changes"}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
